import { BookingRequestSchema, BulkBookingRequestSchema } from '@kouto/types';
import { post } from 'api/api';
import getBookBulkEndpointUrl from 'api/book/getBookBulkEndpointurl';
import { fetchCartStatus } from 'features/ShoppingCart/utils/bookingStatusPolling';
import compose from 'lodash/fp/compose';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import * as types from 'types/booking';
import { CONFIRM_KICC_APPROVAL_FAILURE } from 'types/booking';
import { debug } from 'utils/debug';
import fetchApi from 'utils/fetch';
import { getDeviceType } from '../utils';
import * as helpers from './helpers';

// currently this is to get only the adobeId, but it might be extended to get other external analytics ids in the future
export const getExternalAnalyticsUserId = () => {
  try {
    const cookies = document.cookie.split('; ');
    const amcvCookie = cookies.find((c) => /^AMCV_[^=]+%40AdobeOrg=/.test(c));

    const adobeId = (() => {
      if (!amcvCookie) return null;

      const [, value] = amcvCookie.split('=');
      const decodedValue = decodeURIComponent(value);
      const match = decodedValue.match(/MCMID\|([^|]+)/);

      if (!match) return null;
      const parsedValue = match[1];
      return parsedValue;
    })();

    return adobeId;
  } catch (error) {
    return null;
  }
};

export const setBooking = (values) => ({
  type: types.SET_BOOKING,
  payload: values,
});

export const resetBooking = () => ({
  type: types.RESET_BOOKING,
});

/**
 * Create
 */
const createBookingRequest = ({ booking, options }) => ({
  type: types.CREATE_BOOKING_REQUEST,
  payload: {
    booking,
    options,
  },
});

const createBookingSuccess = ({ booking, response }) => ({
  type: types.CREATE_BOOKING_SUCCESS,
  payload: {
    booking,
    response,
  },
});

const bookingFields = [
  'sessionTime',
  'purchaser',
  'participants',
  'mode',
  'notes',
  'duration',
  'couponCode',
  'accessCode',
  'paymentMethod',
  'paymentDetail',
];

const bookingParticipantFields = [
  'firstName',
  'lastName',
  'emailAddress',
  'phoneNumber',
  'priceTierName',
  'customQuestionResponses',
];

export const filter = (body) => {
  const result = pick(body, bookingFields);
  result.purchaser = pick(body, bookingParticipantFields);
  result.participants = result.participants.map((participant) => {
    return pick(participant, bookingParticipantFields);
  });
  return result;
};

export const createBooking =
  (experienceId, body, siteLanguage) => async (dispatch) => {
    const filters = compose(filter, helpers.filterEmpty);
    const bookingData = {
      ...filters(body),
      bookingScriptUrl: window.location.href,
      deviceType: getDeviceType(),
      externalAnalyticsUserId: getExternalAnalyticsUserId() ?? undefined,
      siteLanguage,
    };

    const { success } = BookingRequestSchema.safeParse(bookingData);

    if (!success) {
      return dispatch(
        helpers.errorAction(
          types.CREATE_BOOKING_FAILURE,
          'Invalid booking data!',
        ),
      );
    }

    dispatch(createBookingRequest({ booking: bookingData }));
    try {
      const { data } = await fetchApi
        .post(`v1/experiences/${experienceId}/book`, {
          json: bookingData,
        })
        .json();
      return dispatch(createBookingSuccess({ booking: body, response: data }));
    } catch (errorResponse) {
      debug('error', { createBookingError: errorResponse });
      return dispatch(
        helpers.errorAction(types.CREATE_BOOKING_FAILURE, errorResponse),
      );
    }
  };

const bulkBookingFields = [
  'purchaser',
  'data',
  'couponCode',
  'accessCode',
  'paymentMethod',
  'paymentDetail',
  'additionalCustomQuestionResponses',
];

const bulkBookingParticipantFields = [
  'firstName',
  'lastName',
  'priceTierName',
  'emailAddress',
  'phoneNumber',
  'customQuestionResponses',
  'resourceSelections',
];

const integrationsFields = ['integrationType', 'validationData'];
const pmsValidationFields = [
  'reservationId',
  'reservationCode',
  'confirmationId',
];

export const formatBulkBookingCartItemsData = (
  cartItems,
  couponCode,
  creditCodes,
) => {
  return cartItems.map((item) => {
    const dicountedExperienceIds = couponCode?.experienceIds || [];
    const isExperienceDiscounted = dicountedExperienceIds.includes(
      item.experienceId,
    );
    const isCreditCode = creditCodes && creditCodes.length > 0;
    const appliedCreditCodes = creditCodes?.map((creditCode, index) => ({
      code: creditCode.code,
      appliedValue: creditCode.value,
      sequence: index + 1,
    }));

    return {
      mode: item.bookingMode,
      experienceId: item.experienceId,
      notes: item.notes,
      sessionDuration: item.sessionDuration,
      resourceGroupId: item.groupId,
      resourceQuantity: item.groupId ? item.participants.length : undefined,
      sessionTime: item.sessionDateTime,
      participants: item.participants.map((p) => {
        return pick(p, bulkBookingParticipantFields);
      }),
      additionalCustomQuestionResponses: item.additionalCustomQuestionResponses,
      ...(item.accessCode && {
        accessCode: item.accessCode,
      }),
      ...(isExperienceDiscounted && {
        couponCode: couponCode.code,
      }),
      ...(isCreditCode && {
        appliedCreditCodes,
      }),
      ...(item.waitlistEntryId && {
        waitlistEntryId: item.waitlistEntryId,
      }),
      ...(item?.addOns?.length && {
        listingAddOns: item.addOns.map((addOn) => ({
          listingAddOnId: addOn.id,
          quantity: addOn.selectedNumber,
        })),
      }),
      ...(item.recommendationId && {
        recommendationId: item.recommendationId,
      }),
    };
  });
};

const formatBulkBookingBody = (body) => {
  const result = pick(body, [...bulkBookingFields, ...integrationsFields]);
  result.purchaser = pick(result.purchaser, bookingParticipantFields);
  if (result.paymentDetail) {
    result.validationData = pick(result.validationData, pmsValidationFields);

    result.paymentDetail = {
      pmsType: result.integrationType,
      ...result.paymentDetail,
      ...result.validationData,
      reservationId: result.validationData.reservationId,
      reservationCode:
        result.paymentDetail.reservationCode ||
        result.validationData.confirmationId,
    };

    if (result.paymentMethod === 'member-number') {
      result.paymentDetail = pick(result.paymentDetail, [
        'lastName',
        'methodNumber',
      ]);
    }
  }
  return omit(result, [...integrationsFields, 'couponCode']);
};

const createBulkBookingRequest = ({ booking, options }) => ({
  type: types.CREATE_BULK_BOOKING_REQUEST,
  payload: {
    booking,
    options,
  },
});

const createBulkBookingSuccess = ({ booking, response }) => ({
  type: types.CREATE_BULK_BOOKING_SUCCESS,
  payload: {
    booking,
    response,
  },
});

export const createBulkBooking =
  (brandId, cartId, body, hyattAuthToken, loyaltyProgramData, siteLanguage) =>
  async (dispatch) => {
    const formatFN = compose(formatBulkBookingBody, helpers.filterEmpty);

    const bulkBookingData = {
      ...formatFN(body),
      bookingScriptUrl: window.location.href,
      deviceType: getDeviceType(),
      cartId,
      loyaltyProgramData: loyaltyProgramData ?? undefined,
      externalAnalyticsUserId: getExternalAnalyticsUserId() ?? undefined,
      siteLanguage,
    };

    const { success, error } =
      BulkBookingRequestSchema.safeParse(bulkBookingData);

    debug('error', 'parsing error', { error });
    if (!success) {
      debug('error', 'parsing error', { error, bulkBookingData });
      return dispatch(
        helpers.errorAction(
          types.CREATE_BULK_BOOKING_FAILURE,
          'Invalid booking data!',
        ),
      );
    }

    dispatch(createBulkBookingRequest({ booking: bulkBookingData }));
    try {
      const url = getBookBulkEndpointUrl(brandId);
      const { data } = await post(url, {
        body: JSON.stringify(bulkBookingData),
        headers: {
          Authorization: hyattAuthToken,
          'Kouto-Brand-Id': brandId,
          'Way-Brand-Id': brandId,
          Accept: 'application/json, text/plain, */*',
        },
      });

      return dispatch(
        createBulkBookingSuccess({ booking: body, response: data }),
      );
    } catch (errorResponse) {
      const errorDetails = JSON.parse(errorResponse.message);

      const errAction = helpers.errorAction(
        types.CREATE_BULK_BOOKING_FAILURE,
        errorDetails,
      );
      const { experiences, paymentMethod } = errorDetails?.errors ?? {};

      if (errAction.error === 'Something went wrong') {
        const result = await fetchCartStatus({ cartId });
        return result
          ? dispatch(createBookingSuccess({ booking: body, response: {} }))
          : dispatch({
              ...errAction,
              errorType: errorDetails?.type,
              cartItemsError: paymentMethod?.error
                ? [paymentMethod.error]
                : experiences,
            });
      }

      debug('error', { createBulkBookingError: errorResponse, body });

      return dispatch({
        ...errAction,
        errorType: errorDetails?.type,
        cartItemsError: paymentMethod?.error
          ? [paymentMethod.error]
          : experiences,
      });
    }
  };

export const resetCartError = () => ({
  type: types.RESET_CART_ERROR,
});

const createPMSValidationRequest = () => ({
  type: types.CREATE_PMS_VALIDATION_REQUEST,
});

const createPMSValidationSuccess = ({ response }) => ({
  type: types.CREATE_PMS_VALIDATION_SUCCESS,
  payload: {
    response,
  },
});

export const validateRoomChargeAction =
  ({ brandId, payload, integrationType }) =>
  async (dispatch) => {
    dispatch(createPMSValidationRequest());

    try {
      const { data } = await fetchApi
        .post(`v1/brands/${brandId}/room-charge/${integrationType}/validate`, {
          json: {
            ...(payload.methodNumber && {
              reservationNumber: payload.methodNumber,
            }),
            ...omit(payload, 'methodNumber'),
          },
        })
        .json();
      if (!data) {
        return dispatch(
          helpers.errorAction(types.CREATE_PMS_VALIDATION_FAILURE, 'error'),
        );
      }

      return dispatch(createPMSValidationSuccess({ response: data }));
    } catch (errorResponse) {
      return dispatch(
        helpers.errorAction(types.CREATE_PMS_VALIDATION_FAILURE, errorResponse),
      );
    }
  };

const getBookingContextRequest = () => ({
  type: types.GET_BOOKING_CONTEXT_REQUEST,
});

const getBookingContextSuccess = ({ response }) => ({
  type: types.GET_BOOKING_CONTEXT_SUCCESS,
  payload: {
    response,
  },
});

export const getBookingContext = (cartId) => async (dispatch) => {
  dispatch(getBookingContextRequest());
  try {
    const { data } = await fetchApi.get(`v1/carts/${cartId}`).json();
    if (!data) {
      return dispatch(
        helpers.errorAction(types.GET_BOOKING_CONTEXT_FAILURE, 'error'),
      );
    }
    return dispatch(getBookingContextSuccess({ response: data }));
  } catch (errorResponse) {
    return dispatch(
      helpers.errorAction(types.GET_BOOKING_CONTEXT_FAILURE, errorResponse),
    );
  }
};

export const confirmKICCApproval = async ({ brandId, ...approvalMeta }) => {
  try {
    const { data } = await fetchApi
      .post(`v1/brands/${brandId}/integrations/kicc/approve`, {
        json: {
          ...approvalMeta,
        },
      })
      .json();

    return {
      status: data.status,
      error: null,
    };
  } catch (errorResponse) {
    return Promise.resolve({
      status: CONFIRM_KICC_APPROVAL_FAILURE,
      error: errorResponse.message,
    });
  }
};
